import React from 'react';
import {detect} from 'detect-browser';
import clsx from 'clsx';
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  Divider,
  Tooltip,
  Typography,
  Button,
  Grid,
  Avatar,
  IconButton,
  Icon,
} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import {
  Poll as PollIcon,
  ExpandMore,
  ExpandLess,
  AccountBox as AccountBoxIcon,
  Computer,
  Launch as LaunchIcon,
  Dns as DnsIcon,
  CreditCard as CreditCardIcon,
  ReportProblem as ReportProblemIcon,
  ListAlt,
  Description as DescriptionIcon,
  PlaylistAdd as PlaylistAddIcon,
  LiveHelp,
  Assignment as AssignmentIcon,
  ConfirmationNumber,
  Security as SecurityIcon,
  CancelPresentation,
  TrendingUp as TrendingUpIcon,
  SwapHorizontalCircle as SwapHorizontalCircleIcon,
  Euro as EuroIcon,
  MailOutline as MailOutlineIcon,
  Close as CloseIcon,
  NewReleases as NewReleasesIcon,
} from '@material-ui/icons';
import {Link} from 'react-router-dom';
import ReactGA from 'react-ga4';
import moment from 'moment';
import lscache from 'lscache';
import copyToClipboard from 'copy-to-clipboard';

import useStore from '../store/store';
import AnalyticsIcon from './img/ic_analytics.svg';
import AdManagerLogo from './img/ad-manager-logo.svg';
import testRecommendations from './img/testRecommendations.png';
import {AdsTXTQuickStats} from './subcomponents/AdsTxtQuickStats';
import {AdsTXTQuickStatsSelfServe} from './subcomponents/AdsTxtQuickStatsSelfServe';
import {OrderGeneratorQuickStats} from './subcomponents/OrderGeneratorQuickStats';
import {TicketsQuickStats} from './subcomponents/tickets/ticketsQuickStats';
import {ReportsQuickStats} from './subcomponents/ReportsQuickStats';
import ReviewUsBlock from './subcomponents/ReviewUsBlock';
import SelfServePromo from './subcomponents/SelfServePromo';
import LatestNews from './subcomponents/LatestNews';
import {MCM} from '../pages/MCM';

const drawerWidth = 300;

const useStyles = makeStyles(theme => ({
  drawer: {
    flexShrink: 0,
    width: drawerWidth,
    [theme.breakpoints.up('sm')]: {
      width: drawerWidth,
      flexShrink: 0,
    },
  },
  drawerPaper: {
    width: drawerWidth,
    [theme.breakpoints.down('sm')]: {
      width: '100vw',
    },
  },
  drawerOpen: {
    overflow: detect() && detect().name === 'safari' ? 'visible' : 'scroll',
    width: drawerWidth,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    [theme.breakpoints.mobile]: {
      width: '100vw',
    },
  },
  drawerClose: {
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: 0,
    [theme.breakpoints.up('lg')]: {
      width: theme.spacing(9) + 1,
    },
  },
  toolbar: {
    height: 70,
  },
  listItem: {
    paddingLeft: theme.spacing(3),
    height: theme.spacing(8),
  },
  nested_first: {
    paddingLeft: theme.spacing(3),
    height: theme.spacing(8),
  },
  onboardingTooltip: {
    maxWidth: 'none',
    left: -10,
    boxShadow: '5px 5px 70px rgb(32 181 196 / 50%)',
    animation: '$slideLeftBack 5s ease infinite',
  },
  '@keyframes slideLeftBack': {
    '0%': {boxShadow: '5px 5px 70px rgb(32 181 196 / 50%)'},
    '50%': {boxShadow: '5px 5px 70px rgb(32 181 196 / 10%)'},
    '100%': {boxShadow: '5px 5px 70px rgb(32 181 196 / 50%)'},
  },
  managerGridWrapper: {
    padding: 20,
    borderRadius: 8,
    background: theme.palette.background.default,
    cursor: 'pointer',
  },
}));

const Sidebar = ({
  drawerOpen,
  setDrawerOpen,
  isMobile,
  isTablet,
  auth,
  visiblePopup,
  billingDetailsStatus,
  locationHook,
}) => {
  const classes = useStyles();
  const openedSidebarSection = useStore(state => state.openedSidebarSection);
  const onboardingSteps = useStore(state => state.onboardingSteps);
  const selection = useStore(state => state.selection);
  const setState = useStore(state => state.setState);

  // Local state for Sidebar-specific UI
  const [openPlacement, setOpenPlacement] = React.useState(false);
  const [prebidPanelTooltipEnabled, setPrebidPanelTooltipEnabled] = React.useState(true);
  const [showRecommendations, setShowrecommendations] = React.useState(true);

  const isSelfServe = auth?.type >= 5 && auth?.type < 8;

  if (isMobile) {
    return null;
  }

  return (
    <Drawer
      className={clsx(classes.drawer, {
        [classes.drawerOpen]: drawerOpen,
        [classes.drawerClose]: !drawerOpen,
      })}
      classes={{
        paper: clsx({
          [classes.drawerOpen]: drawerOpen,
          [classes.drawerClose]: !drawerOpen,
        }),
      }}
      variant="permanent"
      open={drawerOpen && !isMobile && !isTablet}
    >
      <div>
        <div className={classes.toolbar} />

        <List disablePadding>
          <ListItem
            button
            className={classes.listItem}
            component={Link}
            to={'/home'}
            onClick={() => {
              openedSidebarSection !== 'reporting'
                ? setState('openedSidebarSection', 'reporting')
                : setState('openedSidebarSection', false);

              ReactGA.event({
                category: 'Navigation menu',
                action: 'click',
                label: 'reporting',
              });
            }}
          >
            <ListItemIcon>
              <PollIcon />
            </ListItemIcon>
            <ListItemText primary={'Reporting'} />
            {!auth.menu.error && (
              <>{openedSidebarSection !== 'reporting' ? <ExpandMore /> : <ExpandLess />}</>
            )}
          </ListItem>
          <Collapse in={openedSidebarSection === 'reporting'}>
            {auth && auth.menu && !auth.menu.error && auth.menu.menu && auth.menu.menu[1] && (
              <>
                {auth.google_analytics_enabled === 1 && (
                  <>
                    <ListItem
                      dense
                      button
                      className={classes.nested_first}
                      component={Link}
                      to={`/google-analytics`}
                      onClick={() => {
                        ReactGA.event({
                          category: 'Navigation menu',
                          action: 'click',
                          label: 'google analytics',
                        });
                      }}
                    >
                      <ListItemIcon>
                        <Icon>
                          <img src={AnalyticsIcon} style={{height: 24}} />
                        </Icon>
                      </ListItemIcon>
                      <ListItemText primary={'Google Analytics'} secondary={'Settings'} />
                    </ListItem>
                  </>
                )}

                <ListItem
                  dense
                  button
                  className={classes.nested_first}
                  component={Link}
                  onClick={() => {
                    setOpenPlacement(false);
                    ReactGA.event({
                      category: 'Navigation menu',
                      action: 'click',
                      label: 'account',
                    });
                  }}
                  to={`/home`}
                >
                  <ListItemIcon>
                    <AccountBoxIcon />
                  </ListItemIcon>
                  <ListItemText primary={'Total'} secondary={'Inventory'} />
                </ListItem>
                {auth.menu.menu[1].children.map((item, index) => {
                  return (
                    <ListItem
                      key={`${item.path[1]}-${item.path[2]}-${index}`}
                      dense
                      button
                      selected={selection?.value?.domain === item.path[2]}
                      className={classes.nested_first}
                      onClick={() => {
                        setOpenPlacement(
                          openPlacement && openPlacement.path[2] === item.path[2] ? false : item
                        );

                        setState('calculated', null);

                        setState('selection', {
                          value: {
                            publisher: item.path[1],
                            domain: item.path[2],
                            placemen: null,
                          },
                        });

                        ReactGA.event({
                          category: 'Navigation menu',
                          action: 'click',
                          label: 'domain',
                        });
                      }}
                      component={Link}
                      to={`/home/${item.path[1]}/${item.path[2]}`}
                    >
                      <ListItemIcon>
                        <Computer />
                      </ListItemIcon>
                      <ListItemText primary={item.data.menu.alias} secondary={'Domain'} />
                      <img
                        alt={item.data.menu.title}
                        src={`https://www.google.com/s2/favicons?domain=${item.data.menu.title}`}
                      />
                    </ListItem>
                  );
                })}
              </>
            )}
          </Collapse>

          <Divider />

          {(auth.adserver === 1 || auth.prebid_panel === 1) && (
            <>
              <Tooltip
                open={
                  prebidPanelTooltipEnabled &&
                  !locationHook.pathname?.includes('prebid-panel') &&
                  auth?.onboarding_enabled === 1 &&
                  auth?.onboarding_completed &&
                  !auth.adserver_onboarding_completed &&
                  !auth.wb_auth &&
                  isSelfServe
                }
                interactive
                classes={{tooltip: classes.onboardingTooltip}}
                title={
                  <div style={{padding: 20}}>
                    <Typography gutterBottom style={{paddingBottom: 8}}>
                      👋 Continue onboarding in Prebid Panel.
                    </Typography>
                    <Button
                      variant={'contained'}
                      color={'primary'}
                      size={'small'}
                      startIcon={<LaunchIcon />}
                      component={Link}
                      to={`/prebid-panel`}
                      onClick={() => setDrawerOpen(false)}
                    >
                      Go to Prebid Panel
                    </Button>
                    <Button
                      variant={'text'}
                      color={'primary'}
                      size={'small'}
                      style={{marginLeft: 6}}
                      onClick={() => {
                        setPrebidPanelTooltipEnabled(false);
                      }}
                    >
                      Close
                    </Button>
                  </div>
                }
                placement={'right'}
                arrow
              >
                <ListItem
                  button
                  className={classes.listItem}
                  component={Link}
                  to={`/prebid-panel`}
                  onClick={() => {
                    setDrawerOpen(false);
                    setState('openedSidebarSection', false);
                    ReactGA.event({
                      category: 'Navigation menu',
                      action: 'click',
                      label: 'Prebid Panel',
                    });

                    // Autocomplete main onboarding
                    if (
                      auth?.onboarding_enabled === 1 &&
                      !auth?.onboarding_completed &&
                      onboardingSteps[5]?.active &&
                      isSelfServe
                    ) {
                      setState('completedStep', 5);
                    }
                  }}
                >
                  <>
                    <ListItemIcon>
                      <DnsIcon />
                    </ListItemIcon>

                    <ListItemText primary={'Prebid Panel'} />
                  </>
                </ListItem>
              </Tooltip>
              <Divider />
            </>
          )}

          {isSelfServe && auth?.type !== 7 && (
            <>
              <ListItem
                button
                className={classes.listItem}
                component={Link}
                to={`/subscription`}
                onClick={() => {
                  setState('openedSidebarSection', false);
                  //setDrawerOpen(false);
                  ReactGA.event({
                    category: 'Navigation menu',
                    action: 'click',
                    label: 'Subscription',
                  });
                }}
              >
                <ListItemIcon>
                  <CreditCardIcon />
                </ListItemIcon>
                <ListItemText primary={'Subscription'} />
              </ListItem>

              <Divider />
            </>
          )}

          {/* Disable billing for non-Enterprise SS clients */}
          {auth.billingEnabled === 1 && (!isSelfServe || auth.type === 7) && (
            <>
              <ListItem
                button
                className={classes.listItem}
                component={Link}
                to={`/billing`}
                onClick={() => {
                  ReactGA.event({
                    category: 'Navigation menu',
                    action: 'click',
                    label: 'billing',
                  });
                }}
              >
                <ListItemIcon>
                  <CreditCardIcon />
                </ListItemIcon>
                <ListItemText primary={'Billing'} />
                {billingDetailsStatus.success === false &&
                  auth.username !== 'demo' &&
                  !visiblePopup && (
                    <Tooltip title={'Please fill all billing details'}>
                      <div>
                        <ReportProblemIcon style={{color: 'red', top: 2, position: 'relative'}} />
                      </div>
                    </Tooltip>
                  )}
              </ListItem>
              <Divider />
            </>
          )}

          {auth?.discrepancies_enabled === 1 && (
            <>
              <ListItem
                button
                component={Link}
                className={classes.listItem}
                to={`/discrepancies`}
                onClick={() => {
                  setState('openedSidebarSection', false);
                }}
              >
                <ListItemIcon>
                  <SwapHorizontalCircleIcon />
                </ListItemIcon>
                <ListItemText primary={'Discrepancies'} />
                <Tooltip title="New!" arrow>
                  <NewReleasesIcon
                    style={{
                      color: '#56cb80',
                      marginLeft: '10px',
                    }}
                  />
                </Tooltip>
              </ListItem>
              <Divider />
            </>
          )}

          {auth.inventory === 1 && (
            <>
              <ListItem
                button
                className={classes.listItem}
                component={Link}
                to={`/inventory`}
                onClick={() => {
                  ReactGA.event({
                    category: 'Navigation menu',
                    action: 'click',
                    label: 'inventory',
                  });
                }}
              >
                <ListItemIcon>
                  <ListAlt />
                </ListItemIcon>
                <ListItemText primary={'Inventory'} />
              </ListItem>
              <Divider />
            </>
          )}

          <>
            <ListItem
              button
              className={classes.listItem}
              component={Link}
              to={`/cmp`}
              onClick={() => {
                setState('openedSidebarSection', false);
                setDrawerOpen(false);
                ReactGA.event({
                  category: 'Navigation menu',
                  action: 'click',
                  label: 'CMP',
                });
              }}
            >
              <ListItemIcon>
                <DescriptionIcon />
              </ListItemIcon>
              <ListItemText primary={'CMP'} />
            </ListItem>

            <Divider />
          </>

          <div style={{position: 'relative'}}>
            <ListItem
              button
              component={Link}
              className={classes.listItem}
              to={`/ads-txt`}
              onClick={() => {
                setState('openedSidebarSection', false);
                setDrawerOpen(false);
                ReactGA.event({
                  category: 'Navigation menu',
                  action: 'click',
                  label: 'ads-txt',
                });
              }}
            >
              <ListItemIcon>
                <PlaylistAddIcon />
              </ListItemIcon>
              <ListItemText primary={'Ads.txt'} />
            </ListItem>

            <div style={{display: drawerOpen && !visiblePopup ? 'block' : 'none'}}>
              {auth?.menu &&
              auth?.type &&
              auth.menu.menu &&
              auth.menu.menu[1] &&
              !isMobile &&
              auth.accountId ? (
                isSelfServe ? (
                  <AdsTXTQuickStatsSelfServe key={auth.accountId} />
                ) : (
                  <AdsTXTQuickStats key={auth.accountId} />
                )
              ) : null}
            </div>
            <Divider />
          </div>

          {auth.blocklist === 1 && (
            <>
              <ListItem
                button
                component={Link}
                className={classes.listItem}
                to={`/block-list`}
                onClick={() => {
                  ReactGA.event({
                    category: 'Navigation menu',
                    action: 'click',
                    label: 'block-list',
                  });
                  setState('openedSidebarSection', false);
                }}
              >
                <ListItemIcon>
                  <CancelPresentation />
                </ListItemIcon>
                <ListItemText primary={'Prebid Blocklist'} />

                {isSelfServe && (
                  <Tooltip title="New!" arrow>
                    <NewReleasesIcon
                      style={{
                        color: '#56cb80',
                        marginLeft: '10px',
                      }}
                    />
                  </Tooltip>
                )}
              </ListItem>

              <Divider />
            </>
          )}

          {/* Enable ad manager for Self Serve clients */}
          {isSelfServe && (
            <div style={{position: 'relative'}}>
              <ListItem
                button
                className={classes.listItem}
                component={Link}
                to={`/ad-manager`}
                onClick={() => {
                  setState('openedSidebarSection', false);
                  //setDrawerOpen(false);
                  ReactGA.event({
                    category: 'Navigation menu',
                    action: 'click',
                    label: 'ad manager',
                  });
                }}
              >
                <ListItemIcon>
                  <img alt={'Google Ad Manager'} src={AdManagerLogo} style={{height: 24}} />
                </ListItemIcon>
                <ListItemText primary={'Google Ad Manager'} />
              </ListItem>

              {drawerOpen && <OrderGeneratorQuickStats />}

              <Divider />
            </div>
          )}

          {auth.MCM_enabled === 1 && (
            <>
              <ListItem
                button
                className={classes.listItem}
                component={Link}
                to={`/mcm`}
                onClick={() => {
                  setState('openedSidebarSection', false);
                  ReactGA.event({
                    category: 'Navigation menu',
                    action: 'click',
                    label: 'MCM',
                  });
                }}
              >
                <ListItemIcon>
                  <AssignmentIcon />
                </ListItemIcon>
                <ListItemText primary={'Google MCM'} />

                <div style={{display: drawerOpen ? 'block' : 'none'}}>
                  {auth.menu &&
                    auth.menu.menu &&
                    auth.menu.menu[1] &&
                    !isMobile &&
                    auth.accountId && (
                      <MCM login={auth} sidebar={true} key={auth.accountId} hidden={visiblePopup} />
                    )}
                </div>
              </ListItem>

              <Divider />
            </>
          )}

          {auth.ticketing === 1 && (
            <>
              <ListItem
                button
                className={classes.listItem}
                component={Link}
                to={`/support`}
                onClick={() => {
                  ReactGA.event({
                    category: 'Support',
                    action: 'click',
                    label: 'articles',
                  });
                }}
              >
                <ListItemIcon>
                  <ConfirmationNumber />
                </ListItemIcon>
                <ListItemText primary={'Support'} />
                <TicketsQuickStats />
              </ListItem>

              <Divider />
            </>
          )}

          {auth.knowledge_base_enabled === 1 && (
            <>
              <ListItem
                button
                className={classes.listItem}
                component={Link}
                to={`/knowledge-base`}
                onClick={() => {
                  setState('openedSidebarSection', false);
                  //setDrawerOpen(false);
                  ReactGA.event({
                    category: 'Navigation menu',
                    action: 'click',
                    label: 'articles',
                  });
                }}
              >
                <ListItemIcon>
                  <LiveHelp />
                </ListItemIcon>
                <ListItemText primary={'Knowledge base'} />
              </ListItem>

              <Divider />
            </>
          )}

          <>
            <ListItem
              button
              component={Link}
              className={classes.listItem}
              to={`/blocked-ads`}
              onClick={() => {
                ReactGA.event({
                  category: 'Navigation menu',
                  action: 'click',
                  label: 'ad-reports',
                });
                setState('openedSidebarSection', false);
              }}
            >
              <ListItemIcon>
                <SecurityIcon />
              </ListItemIcon>
              <ListItemText primary={'Blocked ads'} />

              {auth.menu &&
                auth.menu.menu &&
                auth.menu.menu[1] &&
                !isMobile &&
                auth.accountId &&
                drawerOpen && <ReportsQuickStats key={auth.accountId} login={auth} />}
            </ListItem>

            <Divider />
          </>

          {auth.google_analytics_enabled === 1 && auth.yield_formula_enabled === 1 && (
            <>
              <ListItem
                button
                className={classes.listItem}
                component={Link}
                to={`/yield-formula`}
                onClick={() => {
                  setState('openedSidebarSection', false);
                  setDrawerOpen(false);
                  ReactGA.event({
                    category: 'Navigation menu',
                    action: 'click',
                    label: 'Yield Formula',
                  });
                }}
              >
                <ListItemIcon>
                  <TrendingUpIcon />
                </ListItemIcon>
                <ListItemText primary={'Yield Formula'} />
              </ListItem>

              <Divider />
            </>
          )}

          {auth.sales === 1 && (
            <>
              <List disablePadding>
                <ListItem
                  button
                  className={classes.listItem}
                  component={Link}
                  to={`/sales`}
                  onClick={() => {
                    ReactGA.event({
                      category: 'Navigation menu',
                      action: 'click',
                      label: 'sales',
                    });
                  }}
                >
                  <ListItemIcon>
                    <EuroIcon />
                  </ListItemIcon>
                  <ListItemText primary={'Sales'} />
                </ListItem>
              </List>
              <Divider />
            </>
          )}
        </List>

        {drawerOpen && auth && <ReviewUsBlock auth={auth} />}

        {drawerOpen && auth && auth.manager && <SelfServePromo auth={auth} />}

        {auth.manager && drawerOpen && auth.hide_manager !== 1 && (
          <Tooltip title={`Click to copy email ${auth.manager.email} to clipboard`}>
            <Grid
              direction="column"
              onClick={() => {
                ReactGA.event({
                  category: 'Copy Account Executive email',
                  action: 'click',
                });
                copyToClipboard(auth.manager.email);
              }}
              justifyContent="center"
              alignItems="center"
              spacing={1}
              container
              className={classes.managerGridWrapper}
            >
              <Grid item>
                <Typography color={'textSecondary'}>Your Account Executive</Typography>
              </Grid>
              <Grid item>
                <Avatar
                  style={{height: 80, width: 80}}
                  src={`https://wb.setupad.com/managers/${auth.manager.id}.jpeg`}
                />
              </Grid>
              <Grid item>
                <Typography>{auth.manager.fullName}</Typography>
              </Grid>
              <Grid item>
                <Typography
                  variant={'subtitle2'}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                  color={'textSecondary'}
                >
                  <MailOutlineIcon style={{marginRight: 5}} />
                  {auth.manager.email}
                </Typography>
              </Grid>
            </Grid>
          </Tooltip>
        )}

        <Divider />

        {(onboardingSteps[7]?.active ||
          (showRecommendations &&
            auth.type === 1 &&
            drawerOpen &&
            moment().diff(auth.creationDate, 'days') < 60 &&
            !lscache.get('testRecommendations'))) && (
          <>
            <div
              style={{
                textAlign: 'center',
                position: 'relative',
                marginTop: 20,
                marginBottom: 20,
              }}
            >
              <a
                target={'_blank'}
                href={'https://client.setupad.com/Setupad_HB_test_2022.pdf'}
                onClick={() => setState('completedStep', 7)}
              >
                <IconButton
                  size={'small'}
                  style={{position: 'absolute', top: 6, right: 26}}
                  onClick={e => {
                    e.preventDefault();
                    lscache.set('testRecommendations');
                    setShowrecommendations(false);
                  }}
                >
                  <CloseIcon />
                </IconButton>
                <img src={testRecommendations} style={{borderRadius: 4}} />
              </a>
            </div>
            <Divider />
          </>
        )}

        {drawerOpen && <LatestNews />}
      </div>
    </Drawer>
  );
};

export default Sidebar;
